import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { AuthData, AuthContextType } from '../models/user';
import authService from '../services/authService';

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // 初期状態でローカルストレージから認証情報を取得
  const [authData, setAuthData] = useState<AuthData>(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    return {
      token,
      userId,
    };
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // ページ読み込み時に認証状態を確認
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');

      if (token && userId) {
        // トークンの有効性を確認
        const isValidToken = await authService.verifyToken(token);

        if (isValidToken) {
          setAuthData({ token, userId });
        } else {
          // トークンが無効な場合、ローカルストレージをクリア
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          setAuthData({ token: null, userId: null });
        }
      }

      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = (token: string, userId: string) => {
    setAuthData({ token, userId });
    localStorage.setItem('token', token); // ページリロード時にも保持
    localStorage.setItem('userId', userId);
  };

  const logout = () => {
    setAuthData({ token: null, userId: null });
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    window.location.href = '/';
  };

  // ローディング中は何も表示しない
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">認証状態を確認中...</div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ authData, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
